/**
 * page-heading.js — the channel that carries a screen's title AND its action
 * buttons up into the topbar.
 *
 * WHY THIS EXISTS
 * Every screen renders `<PageHeader title subtitle actions />` at the top of
 * its own content, which put the page's name and its "New Invoice" button in a
 * band BELOW the topbar. Two stacked header rows, one of them nearly empty.
 * The heading now travels the other way: PageHeader publishes it here and
 * AppShell's topbar renders it. It is a context rather than a prop because the
 * shell is an <Outlet /> parent — it cannot see the routes below it, and
 * threading a title through every route element would mean touching all thirty
 * screens.
 *
 * TITLE AND ACTIONS TRAVEL DIFFERENTLY, AND THEY HAVE TO
 * The title and subtitle are strings, so they go through React state: publish
 * them in an effect, re-render the shell, done.
 *
 * `actions` is a React element, and a screen builds a fresh one on every
 * render. Putting THAT in state is an infinite loop — publish, shell
 * re-renders, screen re-renders, new element, publish again — so the actions
 * are portalled instead: the topbar exposes an empty <div>, and PageHeader
 * renders its buttons into that DOM node. No shell state, no loop, and the
 * buttons keep the screen's own props, handlers and React context.
 *
 * The slot node is held in state rather than a ref on purpose. Refs are
 * attached after children have already rendered, so a ref would hand
 * PageHeader `null` on the first pass and never re-render it; a state setter
 * used as a callback ref triggers exactly one extra render, and the portal
 * finds its target on the second pass.
 *
 * Outside the shell (sign-in, onboarding) there is no provider, so the context
 * is null and PageHeader draws the whole header itself, exactly as before.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const PageHeadingContext = createContext(null);

/** Read by AppShell's topbar. Null when there is no provider. */
export function usePageHeadingValue() {
  return useContext(PageHeadingContext);
}

/**
 * Publish this screen's heading to the topbar.
 *
 * @returns {{hoisted: boolean, actionsSlot: Element|null}}
 *   `hoisted` is false when there is no shell above this screen, which means
 *   the caller has to render the heading itself.
 */
export function usePublishPageHeading({ title, subtitle }) {
  const context = useContext(PageHeadingContext);
  const publish = context?.publish;

  useEffect(() => {
    if (!publish) return undefined;
    publish({ title, subtitle });
    /* Clearing on unmount matters: without it the last screen's title stays in
       the topbar through the next screen's loading state, which reads as the
       app having navigated somewhere it has not. */
    return () => publish(null);
  }, [publish, title, subtitle]);

  return { hoisted: !!publish, actionsSlot: context?.actionsSlot || null };
}

export function PageHeadingProvider({ children }) {
  const [heading, setHeading] = useState(null);
  const [actionsSlot, setActionsSlot] = useState(null);

  const value = useMemo(
    () => ({ heading, publish: setHeading, actionsSlot, setActionsSlot }),
    [heading, actionsSlot]
  );

  return <PageHeadingContext.Provider value={value}>{children}</PageHeadingContext.Provider>;
}
